import { useCallback } from "react";
import { TripState } from "types/user";
import { tripStateToTripShare } from "libraries/trip";
import useDisplayMessage from "hooks/useDisplayMessage";
import { ROUTE } from "components/base-app/AppRoutes";
import { useAppSelector } from "redux/hooks";
import {
  selectTripTravelMode,
  selectReferenceExperienceId,
  selectReferenceTripDestinations,
  selectReferenceTripLegModes,
} from "redux/slices/shed/shedSlice";
import { EXPERIENCE_ID } from "types/map";

type Props = {
  trip: TripState;
  genericVin: string;
  shareRoute?: string;
  /** When false, omit experienceId from share URL (main `/h/map`). Default true. */
  includeExperience?: boolean;
};

type ReturnType = {
  handleShareTrip: () => void;
};

const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.select();

  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);

  return copied;
};

const useShareTrip = ({
  trip,
  genericVin,
  shareRoute,
  includeExperience = true,
}: Props): ReturnType => {
  const { displayMessage } = useDisplayMessage();
  const experienceId = useAppSelector(selectReferenceExperienceId);
  const travelMode = useAppSelector(selectTripTravelMode);
  const destinations = useAppSelector(selectReferenceTripDestinations);
  const legModes = useAppSelector(selectReferenceTripLegModes);

  const handleShareTrip = useCallback(async () => {
    const tripShare = tripStateToTripShare(
      trip,
      (includeExperience ? experienceId : "") as EXPERIENCE_ID,
      travelMode,
      genericVin,
      destinations,
      legModes
    );

    const shareParams = new URLSearchParams();

    Object.entries(tripShare).forEach(([key, value]) => {
      if (value !== undefined && value !== "") {
        shareParams.append(key, value);
      }
    });

    const shareURL = new URL(window.location.origin + (shareRoute ?? ROUTE.MAP));
    shareURL.search = shareParams.toString();
    const shareURLString = shareURL.toString();

    if (window.navigator.share) {
      try {
        await window.navigator.share({
          text: `From ${tripShare.fromLabel} to ${tripShare.toLabel}`,
          title: "Trip itinerary on Shed",
          url: shareURLString,
        });
      } catch {
        // Share cancelled
      }
      return;
    }

    try {
      const copied = await copyTextToClipboard(shareURLString);
      displayMessage(
        copied
          ? "Share link copied to clipboard"
          : "Could not copy share link",
        copied ? "success" : "error"
      );
    } catch {
      displayMessage("Could not copy share link", "error");
    }
  }, [
    travelMode,
    experienceId,
    includeExperience,
    displayMessage,
    trip,
    genericVin,
    shareRoute,
    destinations,
    legModes,
  ]);

  return { handleShareTrip };
};

export default useShareTrip;
